import { Stack, Text, Loader, Alert, Anchor } from '@mantine/core';
import { Link, useParams } from '@tanstack/react-router';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { usePlaylists } from '../usePlaylists';
import { usePlaylist } from '../usePlaylist';
import { SmartPlaylistContent } from './SmartPlaylistContent';
import { ManualPlaylistContent } from './ManualPlaylistContent';
import type { CustomPlaylistView, TagPlaylistView } from '@/lib/types/Playlist';

export function PlaylistDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const listId = bcLong.getNum(id);

  const { data: playlistsData, isLoading: listsLoading } = usePlaylists();

  // Resolve listType from cached list data
  const allPlaylists = [
    ...(playlistsData?.customLists ?? []),
    ...(playlistsData?.tagLists ?? []),
  ];
  const found = allPlaylists.find(p => p.listId === listId);
  const listType = found?.listType ?? null;

  const { playlist, isLoading: detailLoading, isError, error } = usePlaylist(listId, listType);

  const isLoading = listsLoading || detailLoading;

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading playlist...</Text>
      </Stack>
    );
  }

  if (!listsLoading && !found) {
    return (
      <Stack gap="md">
        <BackLink />
        <Alert icon={<IconAlertCircle size={16} />} title="Not Found" color="orange">
          Playlist not found. It may have been deleted.
        </Alert>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack gap="md">
        <BackLink />
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error?.message ?? 'Failed to load playlist'}
        </Alert>
      </Stack>
    );
  }

  if (!playlist) return null;

  return (
    <Stack gap="md">
      <BackLink />
      {listType === 'tag' ? (
        <SmartPlaylistContent playlist={playlist as TagPlaylistView} />
      ) : (
        <ManualPlaylistContent playlist={playlist as CustomPlaylistView} />
      )}
    </Stack>
  );
}

function BackLink() {
  return (
    <Anchor component={Link} to="/playlists" size="sm">
      <IconArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
      Back to Playlists
    </Anchor>
  );
}
